import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map, catchError } from 'rxjs/operators';
import { of as ObservableOf } from 'rxjs/observable/of';
import { _throw as ObservableThrow } from 'rxjs/observable/throw';
import { UseDiv } from '@blcloud/bl-datamodel/enum/bizcmn/use-div';
import { ApplyPeriodCriteriaDiv } from '@blcloud/bl-datamodel/enum/bizcmn/apply-period-criteria-div';
import { MonthWeekDayDiv } from '@blcloud/bl-datamodel/enum/bizcmn/month-week-day-div';
import { CommonConst } from '@blcloud/bl-datamodel/const/common';
import { IEmployee, ILoginUserEmployeeBindModel, IContactAlertSetting, IContactExecuteItem } from '@blcloud/bl-datamodel';
import { ContactAlertSettingResource, ContactExecuteItemResource } from '@blcloud/bl-ng-resource';
import { BlConditionType, BlApiErrorInfo, HttpStatus } from '@blcloud/bl-ng-common';
import { EmployeeResourceService, LoginResourceService } from '@blcloud/bl-ng-share-module';
import { IContactAlertSettingEx } from './contact-alert-setting-modal.interface';
import { isEmpty as _isEmpty } from 'lodash';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';

/**
 * コンタクトアラート設定サービス
 */
@Injectable()
export class ContactAlertSettingModalService {

  /**
   * コンストラクタ
   * @param
   */
  constructor(
    private contactAlertSettingResource: ContactAlertSettingResource,
    private employeeService: EmployeeResourceService,
    private loginResourceService: LoginResourceService,
    private contactExecuteItemResource: ContactExecuteItemResource,
  ) { }

  /**
   * ログインユーザー情報取得
   */
  getLoginUserEmployeeBindModel(): Observable<ILoginUserEmployeeBindModel> {
    return this.loginResourceService.getLoginUserEmployeeBindModel();
  }

  /**
   * コンタクトアラート設定取得
   * @param code 従業員コード
   */
  getContactAlertSetting(code: string): Observable<IContactAlertSettingEx> {
    return this.contactAlertSettingResource.getById<IContactAlertSetting>(code).pipe(
      map(res => {
        const settingEx = <IContactAlertSettingEx>res;
        settingEx.isRegist = false;

        if (_isEmpty(settingEx.contactExecuteItemCodeList)) {
          settingEx.contactExecuteItemCodeList = [];
        }

        return settingEx;
      }),
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return ObservableOf({
            alertFunctionUseDiv: UseDiv.NotUse,
            contactCheckDivList: [],
            contactExecuteItemCodeList: [],
            applyStartPeriodCriteriaDiv: ApplyPeriodCriteriaDiv.Today,
            applyEndPeriodCriteriaDiv: ApplyPeriodCriteriaDiv.Today,
            applyStartNum: 0,
            applyEndNum: 0,
            monthWeekDayDiv: MonthWeekDayDiv.Day,
            alertPicUseDiv: false,
            employeeName: '',
            applyStartDate: '1900-01-01',
            isRegist: true
          });
        } else {
          return ObservableThrow(error);
        }
      })
    );
  }

  /**
   * コンタクト実施内容のセレクトボックス用データを取得する
   * @param orgCode 組織コード
   */
  getContactExecuteItem(orgCode: string): Observable<{ code: number, name: string, div: number }[]> {
    // 検索条件
    const conditions = [
      {
        key: 'organizationCode',
        value: orgCode,
        type: BlConditionType.Equal
      },
      {
        key: 'logicalDeleteDiv',
        value: LogicalDeleteDiv.Valid,
        type: BlConditionType.Equal
      }];
    // ソート条件
    const sortFields = [
      { field: 'contactExecuteItemDispOrder', desc: false }
    ];

    return this.contactExecuteItemResource.getAll<IContactExecuteItem>({ conditions, sortFields }).pipe(
      map(res => {
        const list = res ? res.searchResultList : [];

        return list.map(contactExecuteItem => {
          return {
            code: contactExecuteItem.contactExecuteItemCode, name: contactExecuteItem.contactExecuteItem,
            div: contactExecuteItem.contactItem.contactCheckDiv
          };
        });
      }),
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return ObservableOf([]);
        } else {
          return ObservableThrow(error);
        }
      })
    );
  }

  /**
   * 従業員コードから従業員を取得します
   * @param code　従業員コード
   */
  getEmployee(code: string): Observable<IEmployee> {
    const id = this.employeeService.getIdString({
      companyMgtCode: CommonConst.COMPANY_MGT_CODE_OWN_COMPANY,
      employeeCode: code
    });

    return this.employeeService.getById<IEmployee>(id).pipe(
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return ObservableOf(null);
        } else {
          return ObservableThrow(error);
        }
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/contact-alert-setting/contact-alert-setting-modal.service.ts